// hooks/useAuthRedirect.js (Alternative approach for route protection)
import { PUBLIC_PATHS } from 'config';
import { PASS_KEY } from 'variables';
import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { getJwtToken } from 'utils/functions/session-storage';
import { getProfile } from 'store/reducers/profile';

const isPublicRoute = (pathname) => {
  return PUBLIC_PATHS.includes(pathname) || pathname.startsWith('/public') || pathname.startsWith('/register');
};

export const useAuthRedirect = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const token = getJwtToken();
  const { profile, isLoading } = useSelector((state) => state.profile);
  const fetchingRef = useRef(false);

  // Reset fetchingRef if token changes (e.g. login/logout)
  useEffect(() => {
    fetchingRef.current = false;
  }, [token]);

  useEffect(() => {
    // 1. Check for missing token on protected routes
    if (!token && !isPublicRoute(pathname)) {
      navigate('/login', { replace: true });
      return; // Stop further checks if redirecting
    }

    // 2. Refetch profile if missing (handle page refresh)
    // We check !profile to see if we need data.
    // We check !fetchingRef.current to avoid double-dispatch in StrictMode or re-renders.
    if (token && !profile && !fetchingRef.current) {
      fetchingRef.current = true;
      dispatch(
        getProfile({
          headers: {
            'Content-Type': 'application/json',
            'Pass-Key': PASS_KEY,
            Authorization: token
          }
        })
      ).catch((err) => {
        console.error('Failed to refetch profile in redirect hook:', err);
        fetchingRef.current = false; // Allow retry on error?
      });
    }

    // 3. License Checks (Only if authenticated and profile loaded)
    // We assume 'isLoading' tracks the fetch status (initially true, or set by getProfile)
    // If we just dispatched, isLoading should be true (or pending).
    // checking !isLoading ensures we wait for result.
    if (token && !isLoading && profile) {
      const hasLicenses = profile.licensetypes && profile.licensetypes.length > 0;
      const isLicensesPage = pathname === '/licenses';

      // if (hasLicenses && isLicensesPage) {
      //   // Licensed users are restricted from the Licenses page
      //   navigate('/dashboard/default', { replace: true });
      // } else if (!hasLicenses && !isLicensesPage && !isPublicRoute(pathname)) {
      if (!hasLicenses && !isLicensesPage && !isPublicRoute(pathname)) {
        // Unlicensed users are restricted to the Licenses page (except public routes)
        navigate('/licenses', { replace: true });
      }
    }
  }, [token, pathname, navigate, profile, isLoading, dispatch]);
};
